import { z } from "zod";

export const agentChatIntentEnum = z.enum([
    "data_query",
    "data_mutation",
    "workflow_explanation",
    "clarification_only",
    "off_topic",
]);
export type AgentChatIntent = z.infer<typeof agentChatIntentEnum>;

export const agentChatInstructionKeyEnum = z.enum([
    "general",
    "reports",
    "transactions",
    "counterparties",
    "accounting",
    "rules",
    "entities",
    "workspaces",
    "mutation-safety",
    "durable-run",
]);
export type AgentChatInstructionKey = z.infer<
    typeof agentChatInstructionKeyEnum
>;

const skillCandidatesSchema = z
    .array(
        z.object({
            name: z.string(),
            why: z.string(),
        }),
    )
    .max(4);

// Router (Hop 1) output
export const intentDetectionResponseSchema = z.object({
    intent_probabilities: z
        .array(
            z.object({
                intent: agentChatIntentEnum,
                probability: z.number().min(0).max(1),
            }),
        )
        // Loose cardinality to keep OpenAI's strict JSON Schema generator happy;
        // service validates argmax/normalization itself.
        .min(1)
        .max(5),
    reasoning: z.string(),
    clarification_needed: z.boolean(),
    clarification_question: z.string().nullable(),
    tool_candidates: z
        .array(
            z.object({
                name: z.string(),
                probability: z.number().min(0).max(1),
                why: z.string(),
            }),
        )
        .max(8),
    instruction_keys: z
        .array(agentChatInstructionKeyEnum.exclude(["durable-run"]))
        .max(8),
    // Required (not optional) so OpenAI strict mode accepts the schema.
    skill_candidates: skillCandidatesSchema,
});
export type IntentDetectionResponse = z.infer<
    typeof intentDetectionResponseSchema
>;

export const persistedIntentSchema = intentDetectionResponseSchema.extend({
    resolvedIntent: agentChatIntentEnum,
    // Rows persisted before skills existed have no skill_candidates.
    skill_candidates: skillCandidatesSchema.default([]),
});
export type PersistedIntent = z.infer<typeof persistedIntentSchema>;

// Processor (Hop 2) output
export const processorResponseSchema = z.object({
    assistant_message: z.string(),
    clarification_question: z.boolean(),
    follow_up_suggestions: z.array(z.string()).max(3).nullable(),
});

export const citationSchema = z.object({
    url: z.string().url(),
    title: z.string(),
});
export type Citation = z.infer<typeof citationSchema>;

// Tool trace (discriminated union)
function hasRecordShape(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

export const toolTraceResultSchema = z.preprocess(
    (value) => {
        if (
            hasRecordShape(value) &&
            typeof value.isError === "boolean" &&
            ("data" in value || "error" in value)
        ) {
            return {
                status: value.isError ? "error" : "success",
                summary: value.isError
                    ? "legacy error result redacted"
                    : "legacy tool result redacted",
            };
        }
        return value;
    },
    z
        .object({
            status: z.enum(["success", "error"]),
            summary: z.string().min(1).max(1000),
        })
        .strict(),
);
export type ToolTraceResult = z.infer<typeof toolTraceResultSchema>;

const toolTraceToolEntrySchema = z.object({
    kind: z.literal("tool"),
    name: z.string(),
    input: z.record(z.unknown()),
    result: toolTraceResultSchema,
    isError: z.boolean(),
    isPreview: z.boolean(),
    durationMs: z.number().int().nonnegative(),
});

const toolTraceWebSearchEntrySchema = z.object({
    kind: z.literal("web_search"),
    searchCallId: z.string(),
    status: z.enum(["completed", "failed"]),
    durationMs: z.number().int().nonnegative().nullable(),
});

export const toolTraceEntrySchema = z.discriminatedUnion("kind", [
    toolTraceToolEntrySchema,
    toolTraceWebSearchEntrySchema,
]);
export type ToolTraceEntry = z.infer<typeof toolTraceEntrySchema>;

// REST response shapes
const isoDateStringSchema = z.string().datetime();

export const agentChatAssistantMessageSchema = z.object({
    id: z.string().uuid(),
    role: z.literal("assistant"),
    content: z.string(),
    intent: persistedIntentSchema.nullable(),
    toolTrace: z.array(toolTraceEntrySchema).nullable(),
    citations: z.array(citationSchema).nullable(),
    clarification: z.boolean(),
    followUpSuggestions: z.array(z.string()).nullable(),
    createdAt: isoDateStringSchema,
});
export type AgentChatAssistantMessage = z.infer<
    typeof agentChatAssistantMessageSchema
>;

// ── Attachments ──────────────────────────────────────────────────────────────

export const agentChatAttachmentInputSchema = z.object({
    financialDocumentId: z.number().int().positive(),
});
export type AgentChatAttachmentInput = z.infer<
    typeof agentChatAttachmentInputSchema
>;

export const agentChatAttachmentPersistedSchema = z.object({
    financialDocumentId: z.number().int().positive(),
    fileName: z.string().min(1),
    mimeType: z.string().min(1),
    uploadedAt: isoDateStringSchema,
});
export type AgentChatAttachmentPersisted = z.infer<
    typeof agentChatAttachmentPersistedSchema
>;

export const agentChatUserMessageSchema = z.object({
    id: z.string().uuid(),
    role: z.literal("user"),
    content: z.string(),
    attachments: z.array(agentChatAttachmentPersistedSchema).max(5).nullable(),
    createdAt: isoDateStringSchema,
});
export type AgentChatUserMessage = z.infer<typeof agentChatUserMessageSchema>;

export const agentChatMessageSchema = z.discriminatedUnion("role", [
    agentChatUserMessageSchema,
    agentChatAssistantMessageSchema,
]);
export type AgentChatMessage = z.infer<typeof agentChatMessageSchema>;

export const agentChatTurnResponseSchema = z.object({
    conversationId: z.string().uuid(),
    assistantMessage: agentChatAssistantMessageSchema,
});

export const conversationSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    createdAt: isoDateStringSchema,
    updatedAt: isoDateStringSchema,
});

export const conversationListResponseSchema = z.object({
    conversations: z.array(conversationSummarySchema),
    totalCount: z.number().int().nonnegative(),
});

export const conversationWithMessagesResponseSchema = z.object({
    conversation: conversationSummarySchema,
    messages: z.array(agentChatMessageSchema),
    messageTotalCount: z.number().int().nonnegative(),
});
